// src/pages/Articles.jsx - Redesigned with Modern Orange-to-Pink Theme
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SearchNavigation from '../components/SearchNavigation';
import ApiService from '../services/apiService';

const Articles = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [blogPosts, setBlogPosts] = useState([]);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      setIsLoading(true);
      const apiResponse = await ApiService.getArticles();
      setBlogPosts(apiResponse || []);
    } catch (error) {
      console.error('Error loading articles:', error);
      setError(error.message || 'Failed to load articles');
    } finally {
      setIsLoading(false);
    }
  }

  const filteredItems = blogPosts.filter(item =>
    item.title.toLowerCase().includes(searchText.toLowerCase()) ||
    item.author.toLowerCase().includes(searchText.toLowerCase()) ||
    item.subject.toLowerCase().includes(searchText.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (dateStr) {
      try {
        // Parse the date and format it nicely
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-IN', {
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        });
      } catch {
        return dateStr;
      }
    }
    return 'Date not available';
  };

  if (isLoading) {
    return (
      <div className="articles-layout">
        <Navbar />
        <SearchNavigation />
        <div className="loading-fullscreen">
          <i className="bx bx-loader-alt bx-spin"></i>
          <p>Loading Articles...</p>
        </div>
        <style>{`
          .articles-layout { min-height: 100vh; background: #fafafa; }
          .loading-fullscreen {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            gap: 15px;
          }
          .loading-fullscreen i { font-size: 60px; color: #E91E63; }
          .loading-fullscreen p { font-size: 18px; color: #666; margin: 0; }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .bx-spin { animation: spin 1s linear infinite; }
        `}</style>
      </div>
    );
  }

  return (
    <div className="articles-layout">
      <Navbar />
      <SearchNavigation />

      <div className="articles-main-content">
        {/* Hero Section */}
        <div className="articles-hero">
          <div className="hero-badge">
            <i className="bx bx-book-reader"></i>
            <span>Legal Articles</span>
          </div>
          <h1 className="hero-title">
            Expert Legal Insights
          </h1>
          <p className="hero-subtitle">
            Read articles from legal experts and practitioners
          </p>
        </div>

        <div className="articles-container">
          {/* Search Card */}
          <div className="search-card">
            <div className="search-card-header">
              <div className="search-icon-wrapper">
                <i className="bx bx-search-alt"></i>
              </div>
              <div className="search-content">
                <h3 className="search-title">Search Articles</h3>
                <p className="search-subtitle">
                  {filteredItems.length} article{filteredItems.length !== 1 ? 's' : ''} available
                </p>
              </div>
            </div>

            <div className="search-card-body">
              <div className="search-box">
                <i className="bx bx-search search-icon"></i>
                <input
                  type="text"
                  className="search-input"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Search by title, author, or subject..."
                />
                {searchText && (
                  <button
                    className="clear-search"
                    onClick={() => setSearchText('')}
                  >
                    <i className="bx bx-x"></i>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Articles Grid */}
          {error ? (
            <div className="error-section">
              <i className="bx bx-error-circle"></i>
              <p>{error}</p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="empty-section">
              <i className="bx bx-search-alt"></i>
              <p>No articles found matching "{searchText}"</p>
              <button 
                className="clear-filter-btn"
                onClick={() => setSearchText('')}
              >
                Clear Search
              </button>
            </div>
          ) : (
            <div className="articles-grid">
              {filteredItems.map((item) => (
                <article key={item.id} className="article-card">
                  <a href={`Article/${item.id}`} className="article-link">
                    <div className="article-image-wrapper">
                      <img 
                        src={`https://legaleagleweb.com/articalsimage/${item.image}`} 
                        alt={item.title}
                        className="article-image"
                        loading="lazy"
                      />
                      <div className="article-overlay">
                        <i className="bx bx-right-arrow-circle"></i>
                      </div>
                    </div>
                    <div className="article-content">
                      <div className="article-subject">
                        <i className="bx bx-bookmark"></i>
                        {item.subject}
                      </div>
                      <h3 className="article-title">{item.title}</h3>
                      <p className="article-excerpt">{item.excerpt}</p>
                      <div className="article-meta">
                        <div className="meta-item">
                          <i className="bx bx-user"></i>
                          <span>{item.author}</span>
                        </div>
                        <div className="meta-item">
                          <i className="bx bx-calendar"></i>
                          <span>{formatDate(item.postDate)}</span>
                        </div>
                      </div>
                      <div className="read-more">
                        <span>Read Article</span>
                        <i className="bx bx-right-arrow-alt"></i>
                      </div>
                    </div>
                  </a>
                </article>
              ))}
            </div>
          )}
        </div>
      </div>

      <style>{`
        /* ============================================
           BASE LAYOUT
        ============================================ */
        .articles-layout {
          min-height: 100vh;
          background: #fafafa;
        }

        .articles-main-content {
          padding-top: 140px;
          width: 100%;
        }

        /* ============================================
           HERO SECTION
        ============================================ */
        .articles-hero {
          background: linear-gradient(135deg, #E91E63 0%, #E91E64 25%, #c41849 75%, #D81B60 100%);
          padding: 50px 20px 40px;
          text-align: center;
          color: white;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 20px;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 50px;
          font-size: 13px;
          font-weight: 600;
          margin-bottom: 16px;
        }

        .hero-badge i {
          font-size: 16px;
        }

        .hero-title {
          font-size: 2rem;
          font-weight: 800;
          margin: 0 0 10px 0;
          text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
          letter-spacing: -0.5px;
          line-height: 1.2;
        }

        .hero-subtitle {
          font-size: 1rem;
          opacity: 0.95;
          margin: 0;
          font-weight: 400;
        }

        /* ============================================
           ARTICLES CONTAINER
        ============================================ */
        .articles-container {
          max-width: 1200px;
          margin: -30px auto 0;
          padding: 0 20px 40px;
          position: relative;
          z-index: 10;
        }

        /* ============================================
           SEARCH CARD
        ============================================ */
        .search-card {
          background: white;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
          margin-bottom: 30px;
        }

        .search-card-header {
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.1), rgba(233, 30, 99, 0.05));
          padding: 24px 28px;
          display: flex;
          align-items: center;
          gap: 16px;
          border-bottom: 2px solid #f5f5f5;
        }

        .search-icon-wrapper {
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, #E91E63, #c41849);
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 24px;
          flex-shrink: 0;
          box-shadow: 0 4px 15px rgba(245, 166, 35, 0.3);
        }

        .search-content {
          flex: 1;
        }

        .search-title {
          font-size: 20px;
          font-weight: 700;
          color: #333;
          margin: 0 0 4px 0;
        }

        .search-subtitle {
          font-size: 13px;
          color: #666;
          margin: 0;
        }

        .search-card-body {
          padding: 24px 28px;
        }

        .search-box {
          position: relative;
        }

        .search-icon {
          position: absolute;
          left: 18px;
          top: 50%;
          transform: translateY(-50%);
          color: #999;
          font-size: 20px;
        }

        .search-input {
          width: 100%;
          padding: 16px 50px 16px 55px;
          border: 2px solid #e0e0e0;
          border-radius: 12px;
          font-size: 15px;
          color: #333;
          background: #fafafa;
          transition: all 0.3s ease;
        }

        .search-input:focus {
          outline: none;
          border-color: #E91E63;
          background: white;
          box-shadow: 0 0 0 4px rgba(245, 166, 35, 0.1);
        }

        .clear-search {
          position: absolute;
          right: 18px;
          top: 50%;
          transform: translateY(-50%);
          width: 32px;
          height: 32px;
          border: none;
          background: #f0f0f0;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #666;
          transition: all 0.2s ease;
        }

        .clear-search:hover {
          background: #e0e0e0;
          color: #333;
        }

        .clear-search i {
          font-size: 20px;
        }

        /* ============================================
           ERROR & EMPTY STATES
        ============================================ */
        .error-section,
        .empty-section {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 80px 20px;
          gap: 15px;
          background: white;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
        }

        .error-section i,
        .empty-section i {
          font-size: 60px;
          color: #E91E63;
        }

        .error-section i {
          color: #c41849;
        }

        .error-section p,
        .empty-section p {
          font-size: 16px;
          color: #666;
          margin: 0;
          text-align: center;
        }

        .clear-filter-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          background: linear-gradient(135deg, #E91E63, #c41849);
          color: white;
          border: none;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-top: 10px;
        }

        .clear-filter-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(233, 30, 99, 0.3);
        }

        /* ============================================
           ARTICLES GRID
        ============================================ */
        .articles-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 24px;
        }

        .article-card {
          background: white;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
          transition: all 0.3s ease;
        }

        .article-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
        }

        .article-link {
          display: block;
          text-decoration: none;
          color: inherit;
        }

        .article-image-wrapper {
          position: relative;
          width: 100%;
          height: 220px;
          overflow: hidden;
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.1), rgba(233, 30, 99, 0.05));
        }

        .article-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: all 0.4s ease;
        }

        .article-card:hover .article-image {
          transform: scale(1.1);
        }

        .article-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.9), rgba(233, 30, 99, 0.9));
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: all 0.3s ease;
        }

        .article-card:hover .article-overlay {
          opacity: 1;
        }

        .article-overlay i {
          font-size: 50px;
          color: white;
          transform: scale(0.5);
          transition: all 0.3s ease;
        }

        .article-card:hover .article-overlay i {
          transform: scale(1);
        }

        .article-content {
          padding: 24px;
        }

        .article-subject {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 14px;
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.1), rgba(233, 30, 99, 0.05));
          border-radius: 50px;
          font-size: 12px;
          font-weight: 600;
          color: #E91E63;
          margin-bottom: 12px;
        }

        .article-subject i {
          font-size: 14px;
        }

        .article-title {
          font-size: 18px;
          font-weight: 700;
          color: #333;
          margin: 0 0 12px 0;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          transition: all 0.2s ease;
        }

        .article-card:hover .article-title {
          color: #E91E63;
        }

        .article-excerpt {
          font-size: 14px;
          color: #666;
          margin: 0 0 16px 0;
          line-height: 1.6;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .article-meta {
          display: flex;
          align-items: center;
          gap: 16px;
          flex-wrap: wrap;
          margin-bottom: 16px;
          padding-bottom: 16px;
          border-bottom: 1px solid #f0f0f0;
        }

        .meta-item {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          color: #666;
        }

        .meta-item i {
          font-size: 16px;
          color: #999;
        }

        .read-more {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          font-weight: 600;
          color: #E91E63;
          transition: all 0.2s ease;
        }

        .article-card:hover .read-more {
          gap: 12px;
        }

        .read-more i {
          font-size: 20px;
          transition: all 0.2s ease;
        }

        /* ============================================
           ANIMATIONS
        ============================================ */
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .bx-spin {
          animation: spin 1s linear infinite;
        }

        /* ============================================
           RESPONSIVE DESIGN
        ============================================ */
        @media (max-width: 768px) {
          .articles-main-content {
            padding-top: 130px;
          }

          .articles-hero {
            padding: 35px 15px 30px;
          }

          .hero-title {
            font-size: 1.6rem;
          }

          .hero-subtitle {
            font-size: 0.95rem;
          }

          .articles-container {
            margin-top: -20px;
            padding: 0 15px 30px;
          }

          .search-card-header {
            padding: 20px 22px;
          }

          .search-icon-wrapper {
            width: 44px;
            height: 44px;
            font-size: 20px;
          }

          .search-title {
            font-size: 18px;
          }

          .search-card-body {
            padding: 20px 22px;
          }

          .articles-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }

          .article-image-wrapper {
            height: 200px;
          }

          .article-content {
            padding: 20px;
          }

          .article-title {
            font-size: 17px;
          }
        }

        @media (max-width: 480px) {
          .hero-title {
            font-size: 1.4rem;
          }

          .hero-badge {
            padding: 6px 14px;
            font-size: 12px;
          }

          .search-card-header {
            padding: 18px 20px;
          }

          .search-icon-wrapper {
            width: 40px;
            height: 40px;
            font-size: 18px;
          }

          .search-title {
            font-size: 16px;
          }

          .search-input {
            padding: 14px 45px 14px 50px;
            font-size: 14px;
          }

          .article-image-wrapper {
            height: 180px;
          }

          .article-content {
            padding: 18px;
          }

          .article-title {
            font-size: 16px;
          }

          .article-excerpt {
            font-size: 13px;
          }

          .article-meta {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }
        }

        @media (min-width: 769px) and (max-width: 1024px) {
          .articles-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (min-width: 1400px) {
          .articles-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }
          /* ============================================
           REMOVE TOP WHITE SPACE
        ============================================ */
        body {
          margin: 0 !important;
          padding: 0 !important;
        }
      `}</style>
    </div>
  );
};

export default Articles;